import { useMemo } from "react";
import { useSelector } from "react-redux";
import {
  getPermissionsForRole,
  userHasAllPermissions,
  userHasAnyPermission,
  userHasPermission,
} from "../config/permissions";
import { ROLES, getRoleLabel, normalizeRole, roleCanAccess } from "../config/roles";

/**
 * Permission-first access control for the ops portal.
 * Falls back to role templates when `user.permissions` is absent (mock / legacy).
 */
export default function usePermissions() {
  const user = useSelector((state) => state.auth.user);
  const role = user?.role;
  const roleKey = normalizeRole(role);
  const permissions = user?.permissions ?? getPermissionsForRole(roleKey || role);

  return useMemo(
    () => ({
      user,
      role: roleKey || role,
      permissions,
      roleLabel: getRoleLabel(roleKey || role),
      isSuperAdmin: roleKey === ROLES.SUPER_ADMIN,
      isExecutiveManager: roleKey === ROLES.EXECUTIVE_MANAGER,
      isCallCenterManager: roleKey === ROLES.CALL_CENTER_MANAGER,
      isCallCenterAgent: roleKey === ROLES.CALL_CENTER_AGENT,
      isLegalOfficer: roleKey === ROLES.LEGAL_OFFICER,
      isReadOnly:
        roleKey === ROLES.EXECUTIVE_MANAGER &&
        !permissions.some((p) => p.endsWith(".update") || p.endsWith(".manage") || p.endsWith(".assign")),
      hasPermission: (permission) => userHasPermission(user, permission),
      hasAnyPermission: (keys) => userHasAnyPermission(user, keys),
      hasAllPermissions: (keys) => userHasAllPermissions(user, keys),
      /** @deprecated prefer hasPermission — kept for gradual migration */
      can: (allowedRoles) => roleCanAccess(roleKey || role, allowedRoles),
    }),
    [permissions, role, roleKey, user]
  );
}
